import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  BULK_PDF_EXPORT_LIMIT,
  FormElement,
  FormElementKind,
  GetRepairsRequestDTO,
  GetRepairsResponseDTO,
  MODULE,
  PdfScope,
  RecordState,
  RepairOrderByFieldEnumSchema,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useParams } from 'react-router';
import { z } from 'zod';

import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import { ShareDialog } from '@/components/common/share-dialog';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import DownloadPdfIcon from '@/components/icons/download-pdf';
import { LinkViewIcon } from '@/components/icons/link-view';
import PencilIcon from '@/components/icons/pencil';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { useTopbarStore } from '@/components/topbar/topbar-store';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { env } from '@/env';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import ShareIcon from '@/legacy/components/icons/share';
import { buildFilterFields } from '@/queries/column-filter';
import { getFormByIdQueryOptions } from '@/queries/forms';
import { getLocationsQueryOptions } from '@/queries/locations';
import { getRepairsInfiniteQueryOptions, repairKeys } from '@/queries/repairs';
import { getUnitsQueryOptions } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useUserStore } from '@/stores/user.store';
import { exportRecordsToPdfZip } from '@/utils/bulk-pdf-export';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';
import { useEditFormNavigate } from '@/utils/routes';
import { useDropTableColumn } from '@/utils/table-meta';

import { NoInspectionsFound, NoRepairFoundFromFiltersDisplay, NoRepairFoundFromSearchDisplay, RepairSubmissionsTable } from './table';
import { FILTER_FIELD_MAP, getElementIdFromColumnId, groupOptions, isFormFieldColumn, columns as repairsColumns } from './table-meta';
import { TopbarActions } from './topbar-actions';

const RepairSubmissionsPage = () => {
  const queryClient = useQueryClient();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();
  const { setModuleFilters } = useModulesStore();

  const { org } = useUserStore();
  const handleEditRepairForm = useEditFormNavigate();
  const { canManageRepairForms, canEditRepairRecord, canExportCsv } = usePermissions();

  const { id: formId } = useParams();

  const { data: formData } = useQuery(getFormByIdQueryOptions({ formId }));

  // Extract form elements from the form
  const formElements = useMemo<FormElement[]>(() => {
    return (formData?.form?.elements as FormElement[]) ?? [];
  }, [formData]);

  // Fetch units for unit reading columns
  const { data: unitsData } = useQuery(getUnitsQueryOptions());

  // Fetch locations for location select columns
  const { data: locationsData } = useQuery(getLocationsQueryOptions({ filters: { state: RecordState.Active } }));

  // Create dynamic columns with form elements, units and locations
  const dynamicColumns = useMemo(() => repairsColumns(formElements, unitsData, locationsData?.data), [formElements, unitsData, locationsData?.data]);

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.RepairSubmissions, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns: dynamicColumns,
  });

  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isDownloadingPdfs, setIsDownloadingPdfs] = useState(false);

  const debouncedSearch = useDebounce(search, 500);

  useDropTableColumn({
    module: MODULE.RepairSubmissions,
    columnIds: ['contact'],
  });

  useLayoutEffect(() => {
    useTopbarStore.getState().setActions(<TopbarActions />);

    return () => {
      useTopbarStore.getState().setActions(<></>);
    };
  }, []);

  // Build formFields filter array for the API from form field column filters
  const formFieldFilters = useMemo(() => {
    if (!filters || !formElements.length) return undefined;

    const formFieldFilterList: { elementId: string; kind: FormElementKind; value: string | string[] | { from: string; to: string } }[] = [];

    for (const f of filters) {
      if (!isFormFieldColumn(f.id)) continue;

      const elementId = getElementIdFromColumnId(f.id);
      const element = formElements.find((e) => e.id === elementId);
      if (!elementId || !element || f.value === undefined || f.value === null || (Array.isArray(f.value) && f.value.length === 0)) continue;

      formFieldFilterList.push({
        elementId,
        kind: element.kind,
        value: f.value as string | string[] | { from: string; to: string },
      });
    }

    return formFieldFilterList.length > 0 ? formFieldFilterList : undefined;
  }, [filters, formElements]);

  // Build orderBy object, handling form field sorting specially
  const orderBy = useMemo(() => {
    if (!sorting.key) return undefined;

    if (isFormFieldColumn(sorting.key)) {
      const elementId = getElementIdFromColumnId(sorting.key);
      const element = formElements.find((e) => e.id === elementId);
      if (!elementId || !element) return undefined;

      return {
        field: 'formField' as const,
        direction: sorting.direction as 'asc' | 'desc',
        formField: { elementId, kind: element.kind },
      };
    }

    return {
      field: sorting.key as z.infer<typeof RepairOrderByFieldEnumSchema>,
      direction: sorting.direction as 'asc' | 'desc',
    };
  }, [sorting.key, sorting.direction, formElements]);

  const {
    data: repairsData,
    fetchNextPage: fetchNextRepairsPage,
    hasNextPage: hasNextRepairsPage,
    isFetching: isFetchingRepairs,
    isFetchingNextPage: isFetchingNextRepairsPage,
    error: repairsError,
  } = useInfiniteQuery(
    getRepairsInfiniteQueryOptions({
      filters: {
        search: debouncedSearch,
        ...buildFilterFields(filters, FILTER_FIELD_MAP, {
          extraFields: { formId: [formId as string], formFields: formFieldFilters },
          dropUndefined: true,
        }),
      },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetRepairsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedRepairs = useMemo(() => {
    return (incomingData: typeof repairsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const repairs = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => repairs[parseInt(id)]);
    };
  }, [selectedRows]);

  const { mutate: deleteRepairs, isPending: isDeletingRepairs } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.DeleteRepairs({ repairIds: ids }),
    onSuccess: () => {
      toast.success('Repairs deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: repairKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDeleteRepairs = () => {
    if (repairsData?.pages) {
      const toDelete = getSelectedRepairs(repairsData);
      deleteRepairs(toDelete.map((repair) => repair.id));
    }
  };

  const selectedRepairs = useMemo(() => getSelectedRepairs(repairsData), [repairsData, getSelectedRepairs]);
  const canBulkActOnSelectedRepairs = selectedRepairs.length > 0 && selectedRepairs.every((repair) => canEditRepairRecord(repair.repairedByUserId));

  const isRegisterPdfExportEnabled = useFeatureFlag('enableRegisterPdfExport');

  function buildRepairsExportRequest(): RegisterExportQuery<'repairs'> {
    const selected = getSelectedRepairs(repairsData);
    return {
      queryType: 'repairs',
      filters: { fields: { id: selected.map((repair) => repair.id) } },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetRepairsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy,
      columns: activeExportColumnIds(columnOrder),
    };
  }

  function handleCsvExport() {
    if (!repairsData?.pages) return;

    openExportTab({ kind: 'csv', request: { ...buildRepairsExportRequest(), filename: buildCsvExportFilename('repairs') } });
  }

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!repairsData?.pages) return;
    const records = getSelectedRepairs(repairsData);
    if ((scope === 'records' || scope === 'register-and-records') && records.length > BULK_PDF_EXPORT_LIMIT) {
      toast.error(`You can export up to ${BULK_PDF_EXPORT_LIMIT} records at a time. Deselect some and try again.`);
      return;
    }
    openExportTab(buildPdfExportJob({ scope, recordType: 'repair', records, register: buildRepairsExportRequest() }));
  }

  async function handlePdfExportZip() {
    if (!repairsData?.pages) return;
    const selected = getSelectedRepairs(repairsData);
    setIsDownloadingPdfs(true);
    try {
      await exportRecordsToPdfZip(selected, 'repairs');
      toast.success('PDF export completed');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to export PDFs');
    } finally {
      setIsDownloadingPdfs(false);
    }
  }

  useEffect(() => {
    if (repairsError) {
      toast.error(repairsError.message);
    }
  }, [toast, repairsError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextRepairsPage,
    hasNextPage: hasNextRepairsPage,
    isFetching: isFetchingRepairs,
    isError: !!repairsError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => repairsData?.pages.flatMap((page) => page.data) ?? [], [repairsData]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedRepairs.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <div className="text-sm">{formData?.form.name}</div>
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.RepairSubmissions, [])}
                    />
                  )}
                </div>

                <div className="flex h-full items-center">
                  <div className="flex items-center">
                    {formData?.form?.id && canManageRepairForms && (
                      <TablePageHeaderActionButton icon={<PencilIcon />} label="Edit Form" onClick={() => handleEditRepairForm(formData?.form?.id)} />
                    )}

                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) =>
                        updatePageConfig({
                          table: { grouping: value ? [value] : [] },
                        })
                      }
                    />
                    <ColumnsDialog<GetRepairsResponseDTO['data'][number]>
                      open={openColumnsDialog}
                      onOpenChange={setOpenColumnsDialog}
                      columns={dynamicColumns}
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: string; active: boolean }> },
                        })
                      }
                    />
                    <TablePageHeaderActionButton
                      onClick={() => setShareUrl(`${env.VITE_FORMS_URL}/${org?.id}?rawRedirect=true`)}
                      icon={<ShareIcon />}
                      label="Share Link"
                    />
                    <TablePageHeaderActionButton
                      onClick={() => window.open(`${env.VITE_FORMS_URL}/${org?.id}?rawRedirect=true`, '_blank')}
                      icon={<LinkViewIcon />}
                      label="Open Mechanic App"
                    />
                  </div>
                </div>
              </>
            )}

            {selectedRepairs.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedRepairs.length} totalCount={repairsData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCsvIcon />} label="Download CSV" onClick={handleCsvExport} />}
                {isRegisterPdfExportEnabled ? (
                  <DownloadPdfMenu recordNoun="repair" onSelect={handlePdfScopeSelect} canExportRegister={canExportCsv} />
                ) : (
                  <TablePageHeaderActionButton icon={<DownloadPdfIcon />} label="Download PDF" onClick={handlePdfExportZip} disabled={isDownloadingPdfs} />
                )}
                {canBulkActOnSelectedRepairs && (
                  <TablePageHeaderActionButton
                    icon={<DeleteIcon className="text-default-red" />}
                    label="Delete"
                    onClick={() => setOpenDeleteConfirmation(true)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <RepairSubmissionsTable
          data={flattenedData}
          loading={(isFetchingRepairs && !isFetchingNextRepairsPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoRepairFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoRepairFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.RepairSubmissions, [])} />
            ) : (
              <NoInspectionsFound />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters: ColumnFiltersState) => setModuleFilters(MODULE.RepairSubmissions, filters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
          columns={dynamicColumns}
        />
        {hasNextRepairsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextRepairsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteRepairs}
        loading={isDeletingRepairs}
        title="Delete repairs?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these repairs?"
      />

      {shareUrl && <ShareDialog url={shareUrl} onOpenChange={() => setShareUrl(null)} open={!!shareUrl} />}
    </div>
  );
};

export default RepairSubmissionsPage;
